/* eslint-disable */

/**
 * Dependencies:
 * - Custom select component
 *
 * Required translation strings:
 * - addToCart
 * - noStock
 * - noVariant
 * - onlyXLeft
 */

if (!customElements.get('bundle-children')) {
  class BundleChildren extends HTMLElement {
    constructor() {
      super();
    }

    connectedCallback() {
      this.init();
    }

    init() {
      this.section = document.querySelector('.js-product');
      this.data = this.getParentProductData();
      this.availableChildEls = this.querySelectorAll('.gg-bundle-child[data-available]');
      this.productForm = this.section.querySelector('.js-product-form-main');
      this.addBtn = this.productForm ? this.productForm.querySelector('[name="add"]') : null;
      this.childVariantsLoadedCount = 0;
      this.parentVariant = this.getInitialParentVariant();
      this.hasNoVariantPicker = !!this.data.defaultVariant;

      // Check for no parent variant picker (if there's a "defaultVariant")
      if (this.hasNoVariantPicker) {
        const imageUrl = this.data.bundleImages[this.data.defaultVariant.id];

        if (imageUrl) {
          this.updateBundleImage(imageUrl);
        }
      }

      document.addEventListener("on:variant:change", this.handleParentVariantChange.bind(this));
      document.addEventListener("on:child-variant:change", this.handleChildVariantChange.bind(this));
      document.addEventListener("on:child-variant:load", this.handleChildVariantChange.bind(this));
    }

    getInitialParentVariant() {
      if (this.hasNoVariantPicker) {
        return this.data.defaultVariant;
      } else {
        const picker = this.section.querySelector('variant-picker');

        if (picker) {
          return picker.variant;
        } else {
          return null;
        }
      }
    }

    getVisibleChildVariants() {
      let childVariants = [];
      const childWrappers = this.querySelectorAll('.js-bundle-child-wrapper[data-visible="true"]');

      childWrappers.forEach(c => {
        const childVariantPickers = c.querySelectorAll('bundle-child-variant-picker');

        childVariantPickers.forEach(child => {
          // Filter out "DECLINE" options
          if (child.variant) {
            childVariants.push({ ...child.variant, is_child: true, bundle_type: c.dataset.bundleType, product: child.data.product });
          }
        });
      });

      return childVariants;
    }

    /**
     * Handles 'on:variant:change' events on the parent variant picker element.
     * @param {object} e - Event object.
     */
    async handleParentVariantChange(e) {
      if (e.target.variant) {
        this.parentVariant = e.target.variant;

        const imageUrl = this.data.bundleImages[e.target.variant.id];

        if (imageUrl) {
          this.updateBundleImage(imageUrl);
        } else {
          const defaultImage = e.target.variant.featured_image?.src || e.target.data.product?.featured_image
          if (defaultImage) {
            this.updateBundleImage(defaultImage);
          }
        }

        // Allow for the relevant bundle children to be visible/hidden
        await new Promise(res => setTimeout(res, 200));
        
        this.sendViewEvent();
      }
    }

    sendViewEvent() {
      const visibleChildVariants = this.getVisibleChildVariants();
      const sendParentVariantData = !visibleChildVariants?.length || visibleChildVariants[0].bundle_type !== "S";
      
      if (!visibleChildVariants.length && !sendParentVariantData) return;
      if (sendParentVariantData && !this.parentVariant) return;

      const variants = sendParentVariantData ? [this.parentVariant, ...visibleChildVariants] : visibleChildVariants;

      gg.sendEvent({
        type: gg.eventTypes.PRODUCT_VIEW,
        product: this.data.product,
        variants
      });
    }

    async handleChildVariantChange(e) {
      // Wait for DOM changes
      await new Promise(res => setTimeout(res, 100));
      
      if (e.type === "on:child-variant:load") {
        this.childVariantsLoadedCount = this.childVariantsLoadedCount + 1;

        // Send view event after all child variants loaded
        if (this.childVariantsLoadedCount === this.availableChildEls.length) {
          this.sendViewEvent();
        }
      } else {
        this.sendViewEvent();
      }

      setTimeout(() => {
        this.updateAddToCartButton();
      }, 100);
    }

    // /**
    //  * Updates the "Add to Cart" button label and disabled state.
    //  */
    updateAddToCartButton() {
      if (!this.productForm) return;

      let status = null;
      const atcButtonTextEl = this.addBtn.querySelector('.js-atc-text') || this.addBtn;
      const atcButtonPriceEl = this.addBtn ? this.addBtn.querySelector('.js-atc-price') : null;

      if (this.hasNoVariantPicker && atcButtonPriceEl) {
        atcButtonPriceEl.textContent = " - " + this.data.formatted[this.data.defaultVariant.id].price;
      }

      // Get all visible items from stack
      const visibleBundleWrappers = this.querySelectorAll('.js-bundle-child-wrapper[data-visible="true"][data-bundle-type="S"]');

      // Check all child elements that are stack items (and not add-ons)
      for (const wrapper of visibleBundleWrappers) {
        const stackChildren = wrapper.querySelectorAll('bundle-child-variant-picker');

        for (const child of stackChildren) {
          const oos = child.variant?.stockStatus === 'Out of stock' && child.variant?.inventoryPolicy !== 'continue'
          if ((!child.variant?.available && child.dataset.type !== "A") || oos) {
            status = 'out_of_stock';
            break;
          }

          if (child.querySelector('input:checked')?.disabled) {
            status = 'unavailable';
            break;
          }
        }
      }

      switch (status) {
        case 'out_of_stock':
          atcButtonTextEl.textContent = theme.strings.noStock;
          this.addBtn.disabled = true;
          break;
        case 'unavailable':
          atcButtonTextEl.textContent = theme.strings.noVariant;
          this.addBtn.disabled = true;
          break;
        default:
          if (visibleBundleWrappers.length) {
            atcButtonTextEl.textContent = this.addBtn.dataset.addToCartText;
            this.addBtn.disabled = false;
          }
      }
    }
   
    updateBundleImage(url) {
      const bundleImgEl = this.section.querySelector('.js-bundle-image');

      if (bundleImgEl) {
        bundleImgEl.setAttribute('srcset', `${url}&width=800 640w, ${url}&width=1125 900w`);
        bundleImgEl.setAttribute('src', `${url}&width=1214`);
        return true;
      }

      return false;
    }

    /**
     * Gets the parent product data.
     * @returns {?object}
     */
    getParentProductData() {
      const dataEl = this.section.querySelector('[type="application/json"][data-product-variant]');
      return JSON.parse(dataEl.textContent);
    }
  }

  customElements.define('bundle-children', BundleChildren);
}
